/**
 * WeeklyDrillCard — "This week's drill" on the Dashboard (W9 rhythm).
 *
 * The officer's one glance: what to run, how hard, what score passes,
 * where I stand. One button. Renders nothing when no drill is set —
 * the dashboard stays clean for solo officers and quiet departments.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportCrash } from '../../lib/crash';
import { useAuth } from '../../contexts/AuthContext';
import { useSessionStore, useSimulationStore } from '../../stores';
import {
  getWeeklyDrillStatus,
  startWeeklyDrill,
  getRhythmStreaks,
  drillTitle,
  type WeeklyDrillStatus,
} from '../../services/weeklyDrill';

export function WeeklyDrillCard() {
  const { user } = useAuth();
  const { officer } = useSessionStore();
  const { initSession, setPhase } = useSimulationStore();
  const navigate = useNavigate();

  const departmentId = (officer as { departmentId?: string | null } | null)?.departmentId ?? null;

  const [status, setStatus] = useState<WeeklyDrillStatus | null>(null);
  const [streak, setStreak] = useState<number>(0);
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!departmentId || !user?.uid) return;
    let cancelled = false;
    getWeeklyDrillStatus(departmentId, user.uid)
      .then((s) => { if (!cancelled) setStatus(s); })
      .catch((e) => { reportCrash(e); /* card simply doesn't render */ });
    getRhythmStreaks(departmentId)
      .then((s) => { if (!cancelled) setStreak(s.officerStreak); })
      .catch((e) => { reportCrash(e); /* streak chip simply doesn't render */ });
    return () => { cancelled = true; };
  }, [departmentId, user?.uid]);

  if (!departmentId || !status) return null;

  const { config, completion } = status;
  const passed = completion?.passed === true;

  const handleRun = async () => {
    setIsStarting(true);
    setError(null);
    try {
      const result = await startWeeklyDrill(departmentId);
      initSession({
        sessionId: result.sessionId,
        officerUid: user?.uid ?? '',
        departmentId,
        scenario: result.scenario as never,
        worldState: result.worldState as never,
      });
      setPhase('active');
      navigate('/sim');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start the drill');
      setIsStarting(false);
    }
  };

  return (
    <div
      data-testid="weekly-drill-card"
      className="mb-6 bg-copapp-navy-card border border-copapp-line rounded-lg p-4"
    >
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px]">
          <p className="text-[10px] text-copapp-400 uppercase tracking-wider font-semibold">
            ◫ This week&apos;s drill · your department
          </p>
          <p className="text-base font-bold text-fg mt-0.5">{drillTitle(config)}</p>
          <div className="flex flex-wrap items-center gap-2 mt-1.5">
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-copapp-navy-abyss text-fg-muted capitalize">
              {config.difficulty}
            </span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-copapp-navy-abyss text-fg-muted">
              Pass: {config.passTarget}/10
            </span>
            {completion ? (
              passed ? (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">
                  ✓ Passed — best {completion.bestScore}/10
                </span>
              ) : (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">
                  Best {completion.bestScore}/10 — keep at it
                </span>
              )
            ) : (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-copapp-navy-abyss text-fg-faint">
                Not run yet
              </span>
            )}
            {streak >= 1 && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-copapp-navy-abyss text-fg-muted">
                {streak}-week streak
              </span>
            )}
          </div>
        </div>

        <button
          onClick={handleRun}
          disabled={isStarting}
          className={`px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors ${
            passed
              ? 'bg-copapp-navy-abyss border border-copapp-line hover:border-copapp-line-bright'
              : 'bg-copapp-700 hover:bg-copapp-600'
          } disabled:opacity-60`}
        >
          {isStarting ? 'Starting…' : passed ? 'Run again' : completion ? 'Try again' : 'Run it'}
        </button>
      </div>
      {error && <p className="text-xs text-red-400 mt-2">{error}</p>}
    </div>
  );
}
